import React, { useState } from 'react'

import {
  Box,
  Button,
  IconButton,
  Popover,
  MenuItem,
  Typography,
  Link,
  CircularProgress,
} from '@material-ui/core'
import { createStyles, makeStyles } from '@material-ui/core/styles'
import DownArrowIcon from '@material-ui/icons/ArrowDropDown'
import UpArrowIcon from '@material-ui/icons/ArrowDropUp'

import { CustomTheme } from '../../theme/utils'

const useStyles = makeStyles((theme: CustomTheme) => {
  return createStyles({
    content: {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
      padding: theme.spacing(0, 6),
      alignItems: 'center',
    },
    multipleButtons: {
      marginRight: theme.spacing(4),
    },
    popOver: {
      borderRadius: theme.spacing(2),
      marginTop: theme.spacing(3),
      '& .MuiPaper-rounded': {
        borderRadius: theme.spacing(2),
      },
      '& .MuiMenuItem-root': {
        padding: theme.spacing(3, 6),
        justifyContent: 'center',
      },
      '& a:hover': {
        textDecoration: 'none',
      },
      '& .space-between': {
        justifyContent: 'space-between',
        padding: theme.spacing(4),
      },
    },
    '& .MuiPaper-rounded': {
      borderRadius: theme.spacing(1),
    },
    menuItem: {
      '&:not(:last-child)': {
        borderBottom: `1px solid ${theme.palette.divider}`,
      },
    },
    iconButton: {
      boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.2)',
      border: `1px solid`,
      borderRadius: '50%',
    },
  })
})

interface Action {
  label?: string
  onClick: () => void
  disabled?: boolean
  isLoading?: boolean
}

interface B2CCardHeaderProps {
  className?: string
  cardId: string
  collapsed?: boolean
  title?:
    | string
    | boolean
    | number
    | {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [K in any]: never
      }
  toggable?: boolean
  onClickCollapse: () => void
  actions?: Array<Action>
  menuActions?: Array<Action>
  menuActionsIcon?: React.ReactNode
  menuActionsButtonText?: string
}

export default function B2CCardHeader({
  className,
  cardId,
  collapsed,
  title,
  toggable,
  onClickCollapse,
  actions,
  menuActions,
  menuActionsIcon,
  menuActionsButtonText,
}: B2CCardHeaderProps) {
  const classes = useStyles()
  const iconType = collapsed ? <DownArrowIcon /> : <UpArrowIcon />
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  function handleClick(event: React.MouseEvent<HTMLElement>) {
    setAnchorEl(event.currentTarget)
  }

  function handleClose() {
    setAnchorEl(null)
  }

  function handleMenuItemClick(action: () => void) {
    if (action) {
      action()
    }
    setAnchorEl(null)
  }

  return (
    <Box className={className}>
      <Box className={classes.content} data-hook={`card-${cardId}-header`}>
        <Box
          height="60px"
          display="flex"
          alignItems="center"
          onClick={() => onClickCollapse()}
          onKeyPress={() => onClickCollapse()}
          data-hook="action-show-subscription-card"
        >
          <Typography variant="h3">{title}</Typography>
          {toggable && iconType}
        </Box>
        <Box display="flex" alignItems="center" justifyContent="end">
          {actions &&
            actions.map((action) => (
              <Button
                className={(actions.length > 1 || menuActions) && classes.multipleButtons}
                size="small"
                variant="outlined"
                color="secondary"
                onClick={action.onClick}
                disabled={action.disabled}
                key={action.label}
              >
                <Typography variant="h5">{action.label}</Typography>
              </Button>
            ))}

          {menuActions && (menuActionsIcon || menuActionsButtonText) && (
            <Box className={className} data-hook="menu-clickable">
              <Box onClick={handleClick}>
                {menuActionsIcon ? (
                  <IconButton className={classes.iconButton} size="small">
                    {menuActionsIcon}
                  </IconButton>
                ) : (
                  <Button variant="outlined" color="secondary" size="small">
                    {menuActionsButtonText}
                  </Button>
                )}
              </Box>

              <Popover
                data-hook={`menu-${cardId}`}
                className={classes.popOver}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                anchorEl={anchorEl}
                getContentAnchorEl={null}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center',
                }}
                elevation={2}
              >
                {menuActions.map((menuAction) => (
                  <Box key={menuAction.label} className={classes.menuItem}>
                    <MenuItem
                      data-hook={`action-${menuAction.label}`}
                      key={menuAction.label}
                      onClick={() => handleMenuItemClick(menuAction.onClick)}
                      disabled={menuAction.disabled}
                    >
                      {menuAction.label && <Link>{menuAction.label}</Link>}
                      {menuAction.isLoading && <CircularProgress size={20} />}
                    </MenuItem>
                    <Box />
                  </Box>
                ))}
              </Popover>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}
